import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import Icon from '@components/atoms/icon/icon';
import PasswordConditions from '@components/vc-auth/register-screen/password-conditions';
import { useAnalytics } from '@context/analytics/analytics.context';
import {
  accountSettingsContactFormEventPayload,
  accountSettingsSaveEventPayload,
} from '@tracking/account-settings/account-settings-tracking';
import styles from './account-details-edit.module.scss';

type AccountDetailsFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

type AccountDetailsEditProps = {
  email: string;
  isPending: boolean;
  onSave: (data: AccountDetailsFormValues) => void;
  apiError?: string | null;
  onErrorReset?: () => void;
};

const AccountDetailsEdit: React.FC<AccountDetailsEditProps> = ({
  email,
  isPending,
  onSave,
  apiError,
  onErrorReset,
}) => {
  const { t } = useTranslation('account-settings');
  const { t: tResetPassword } = useTranslation('reset-password');
  const { sendEvent } = useAnalytics();
  const [showPasswordConditions, setShowPasswordConditions] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isDirty, isValid },
  } = useForm<AccountDetailsFormValues>({
    mode: 'all',
    criteriaMode: 'all',
  });

  React.useEffect(() => {
    if (!apiError) {
      return;
    }
    const subscription = watch(() => onErrorReset?.());
    return () => subscription.unsubscribe();
  }, [apiError, watch, onErrorReset]);

  const newPasswordValue = watch('newPassword');
  const newPasswordRegister = register('newPassword', {
    required: true,
    validate: {
      minLength: (value) => value.length >= 8,
      number: (value) => /\d/.test(value),
      lowercase: (value) => /[a-z]/.test(value),
      uppercase: (value) => /[A-Z]/.test(value),
    },
  });

  const onSubmit = (data: AccountDetailsFormValues) => {
    sendEvent({ type: 'event', payload: { ...accountSettingsSaveEventPayload, property: 'account_details' } });
    onSave(data);
  };

  return (
    <form
      className={styles.accountDetailsEdit}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      data-cy="account-details-form"
    >
      <div className={styles.accountDetailsEdit__field}>
        <label
          htmlFor="email"
          className={styles.accountDetailsEdit__label}
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.EMAIL')}
        </label>
        <input
          id="email"
          type="email"
          className="vc-inputText"
          value={email}
          disabled
          readOnly
        />
        <p className={styles.accountDetailsEdit__hint}>
          <Trans
            ns="account-settings"
            i18nKey="ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.CHANGE_EMAIL_TEXT"
            components={{
              faqLink: (
                <a
                  href={t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.CHANGE_EMAIL_LINK')}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.accountDetailsEdit__hint__faqLink}
                  onClick={() =>
                    sendEvent({
                      type: 'event',
                      payload: { ...accountSettingsContactFormEventPayload, property: 'account_details' },
                    })
                  }
                >
                  {'.'}
                </a>
              ),
            }}
          />
        </p>
      </div>

      <div className={styles.accountDetailsEdit__field}>
        <label
          htmlFor="currentPassword"
          className={styles.accountDetailsEdit__label}
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.CURRENT_PASSWORD')}
        </label>
        <input
          id="currentPassword"
          type="password"
          className={clsx('vc-inputText', errors.currentPassword && styles.accountDetailsEdit__inputError)}
          autoComplete="current-password"
          data-cy="account-details-current-password-input"
          {...register('currentPassword', { required: true })}
        />
        {errors.currentPassword && (
          <span
            role="alert"
            className={styles.accountDetailsEdit__error}
          >
            <Icon
              name="warning-default"
              ariaHidden
            />
            {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.REQUIRED_ERROR')}
          </span>
        )}
      </div>

      <div className={styles.accountDetailsEdit__field}>
        <label
          htmlFor="newPassword"
          className={styles.accountDetailsEdit__label}
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.NEW_PASSWORD')}
        </label>
        <input
          id="newPassword"
          type="password"
          className="vc-inputText"
          autoComplete="new-password"
          data-cy="account-details-new-password-input"
          {...newPasswordRegister}
          onFocus={() => setShowPasswordConditions(true)}
          onBlur={(e) => {
            setShowPasswordConditions(false);
            newPasswordRegister.onBlur(e);
          }}
        />
        {showPasswordConditions && (
          <div className={styles.accountDetailsEdit__passwordConditions}>
            <PasswordConditions
              passwordValue={!!newPasswordValue}
              errorTypes={{
                minLength: !!errors.newPassword?.types?.minLength,
                number: !!errors.newPassword?.types?.number,
                lowercase: !!errors.newPassword?.types?.lowercase,
                uppercase: !!errors.newPassword?.types?.uppercase,
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.accountDetailsEdit__field}>
        <label
          htmlFor="confirmPassword"
          className={styles.accountDetailsEdit__label}
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.CONFIRM_PASSWORD')}
        </label>
        <input
          id="confirmPassword"
          type="password"
          className={clsx('vc-inputText', errors.confirmPassword && styles.accountDetailsEdit__inputError)}
          autoComplete="new-password"
          data-cy="account-details-confirm-password-input"
          {...register('confirmPassword', {
            required: true,
            validate: (value) => value === getValues('newPassword'),
            deps: ['newPassword'],
          })}
        />
        {errors.confirmPassword && (
          <span
            role="alert"
            className={styles.accountDetailsEdit__error}
          >
            <Icon
              name="warning-default"
              ariaHidden
            />
            {errors.confirmPassword.type === 'validate'
              ? tResetPassword('RESET_PASSWORD.NO_MATCH')
              : t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.REQUIRED_ERROR')}
          </span>
        )}
      </div>

      {apiError && (
        <p
          role="alert"
          className={styles.accountDetailsEdit__globalError}
        >
          <Icon
            name="warning-default"
            ariaHidden
          />
          {apiError}
        </p>
      )}

      <div className={styles.accountDetailsEdit__actions}>
        <Button
          variant="primary"
          type="submit"
          size="large"
          disabled={!isDirty || !isValid || isPending}
          data-cy="account-details-save-button"
        >
          {t('ACCOUNT_SETTINGS.ACCOUNT_INFORMATION.SAVE')}
        </Button>
      </div>
    </form>
  );
};

export default AccountDetailsEdit;
